import type { ComponentProps } from 'react';
import { createElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { getFormattedSport, sportIcons } from '@sorare/core/src/lib/sports';

import { SelectHeader } from 'atoms/SelectHeader';

type Props = {
  value: Sport;
  sports?: Sport[];
  onChange: (sport: Sport) => void;
} & Pick<
  ComponentProps<typeof FilterInDropdown>,
  'buttonColor' | 'fullWidth' | 'buttonSize' | 'blur'
>;

const ImageWrapper = styled.div`
  > * {
    max-height: 100%;
    max-width: 100%;
    vertical-align: top;
  }
`;
export const SelectSport = ({
  value,
  sports = [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
  onChange,
  buttonSize = 'small',
  fullWidth = false,
  buttonColor = 'tertiary',
  blur = false,
}: Props) => {
  const iconSize = (() => {
    switch (buttonSize) {
      case 'large':
        return 3;
      case 'medium':
        return 2.5;
      default:
      case 'small':
        return 2;
    }
  })();

  const options = sports.map(sport => ({
    label: (
      <Horizontal as="span" gap={2}>
        <ImageWrapper style={{ height: `calc(${iconSize} * var(--unit))` }}>
          {createElement(sportIcons[sport])}
        </ImageWrapper>
        {getFormattedSport(sport)}
      </Horizontal>
    ),
    value: sport,
  }));

  return (
    <FilterInDropdown
      buttonLabel={
        <Horizontal>
          <ImageWrapper style={{ height: `calc(${iconSize} * var(--unit))` }}>
            {createElement(sportIcons[value])}
          </ImageWrapper>
          {getFormattedSport(value)}
        </Horizontal>
      }
      buttonSize={buttonSize}
      buttonColor={buttonColor}
      fullWidth={fullWidth}
      blur={blur}
    >
      {({ closeDropdown }) => (
        <>
          <SelectHeader>
            <FormattedMessage
              id="SelectSport.title"
              defaultMessage="Select sport"
            />
          </SelectHeader>
          <RadioGroup
            modalSelect
            options={options}
            name="sport-selector"
            value={value}
            onChange={rarity => {
              onChange(rarity);
              closeDropdown();
            }}
          />
        </>
      )}
    </FilterInDropdown>
  );
};
